import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useColorModeValue,
  TableContainer,
  HStack,
  Button,
  Icon,
  useToast,
  VStack,
  Progress,
  Tooltip,
  InputGroup,
  InputLeftElement,
  Input,
  Select,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  IconButton,
} from '@chakra-ui/react';
import {
  FaSync,
  FaExclamationTriangle,
  FaShoppingCart,
  FaSearch,
  FaFilter,
  FaChartLine,
  FaBoxes,
} from 'react-icons/fa';
import analyticsService from '../../services/analyticsService';
import Card from '../card/Cards';

const DynamicStockLevelsWidget = () => {
  const [stockLevels, setStockLevels] = useState([]);
  const [filteredStockLevels, setFilteredStockLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'urgency', direction: 'desc' });
  const [severityFilter, setSeverityFilter] = useState('all');

  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const tableHoverBg = useColorModeValue('gray.50', 'gray.700');
  const criticalStatBg = useColorModeValue('red.50', 'red.900');
  const warningStatBg = useColorModeValue('orange.50', 'orange.900');

  // Calculate urgency score
  const calculateUrgency = (item) => {
    const stockRatio = item.currentStock / item.reorderPoint;
    const safetyRatio = item.currentStock / item.safetyStock;
    
    // Critical: below safety stock
    if (stockRatio < (item.safetyStock / item.reorderPoint)) return { level: 'critical', score: 100, color: 'red' };
    // High: 0-50% of reorder point
    if (stockRatio < 0.5) return { level: 'high', score: 80, color: 'orange' };
    // Medium: 50-75% of reorder point
    if (stockRatio < 0.75) return { level: 'medium', score: 60, color: 'yellow' };
    // Low: 75-100% of reorder point
    if (stockRatio < 1) return { level: 'low', score: 40, color: 'blue' };
    
    return { level: 'adequate', score: 0, color: 'green' };
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await analyticsService.getDynamicStockLevels();
      
      if (response.success) {
        // Enrich data with urgency calculations
        const enrichedData = response.data.map(item => ({
          ...item,
          urgency: calculateUrgency(item),
          stockPercentage: ((item.currentStock / item.reorderPoint) * 100).toFixed(1),
          quantityNeeded: Math.max(0, item.reorderPoint - item.currentStock),
        }));

        // Filter items that need attention (below reorder point or close to it)
        const needsAttention = enrichedData.filter(
          item => item.urgency.level !== 'adequate'
        );

        setStockLevels(needsAttention);
        setFilteredStockLevels(needsAttention);
        setLastRefresh(new Date());
      } else {
        throw new Error(response.message || 'Failed to fetch stock levels.');
      }
    } catch (err) {
      const errorMsg = err.message || 'Error loading dynamic stock levels';
      setError(errorMsg);
      toast({
        title: 'Error Loading Data',
        description: errorMsg,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter and sort logic
  useEffect(() => {
    let filtered = [...stockLevels];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.itemName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Severity filter
    if (severityFilter !== 'all') {
      filtered = filtered.filter(item => item.urgency.level === severityFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === 'urgency') {
        aValue = a.urgency.score;
        bValue = b.urgency.score;
      } else if (sortConfig.key === 'itemName') {
        return sortConfig.direction === 'asc'
          ? a.itemName.localeCompare(b.itemName)
          : b.itemName.localeCompare(a.itemName);
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    setFilteredStockLevels(filtered);
  }, [stockLevels, searchTerm, severityFilter, sortConfig]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast({
      title: 'Stock Levels Updated',
      description: 'Dynamic reorder alerts have been refreshed.',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleReorder = (item) => {
    toast({
      title: 'Reorder Initiated',
      description: `Creating purchase order for ${item.itemName}`,
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
    // Implementation would trigger actual reorder process
  };

  // Summary statistics
  const stats = useMemo(() => {
    const critical = stockLevels.filter(i => i.urgency.level === 'critical').length;
    const high = stockLevels.filter(i => i.urgency.level === 'high').length;
    const totalValue = stockLevels.reduce(
      (sum, item) => sum + (item.quantityNeeded * (item.unitCost || 0)),
      0
    );

    return { critical, high, totalValue };
  }, [stockLevels]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Flex justify="center" align="center" h="300px">
          <VStack>
            <Spinner size="xl" />
            <Text>Loading stock level data...</Text>
          </VStack>
        </Flex>
      );
    }

    if (error) {
      return (
        <Alert status="error">
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="bold">Error Loading Stock Levels</Text>
            <Text fontSize="sm">{error}</Text>
          </Box>
          <Button size="sm" onClick={handleRefresh}>
            Retry
          </Button>
        </Alert>
      );
    }

    if (stockLevels.length === 0) {
      return (
        <Flex justify="center" align="center" h="300px" flexDirection="column">
          <Icon as={FaBoxes} boxSize={12} color="green.500" mb={4} />
          <Text fontSize="lg" fontWeight="semibold" mb={2}>
            All Stock Levels Are Healthy
          </Text>
          <Text fontSize="sm" color="gray.500">
            No items currently require reordering
          </Text>
        </Flex>
      );
    }

    if (filteredStockLevels.length === 0) {
      return (
        <Flex justify="center" align="center" h="300px" flexDirection="column">
          <Icon as={FaSearch} boxSize={12} color="gray.400" mb={4} />
          <Text fontSize="lg" fontWeight="semibold" mb={2}>
            No Items Match Your Filters
          </Text>
          <Text fontSize="sm" color="gray.500">
            Try adjusting your search or filter criteria
          </Text>
        </Flex>
      );
    }

    return (
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Urgency</Th>
              <Th
                cursor="pointer"
                onClick={() => handleSort('itemName')}
                _hover={{ bg: tableHoverBg }}
              >
                Item Name{getSortIcon('itemName')}
              </Th>
              <Th
                isNumeric
                cursor="pointer"
                onClick={() => handleSort('currentStock')}
                _hover={{ bg: tableHoverBg }}
              >
                Current{getSortIcon('currentStock')}
              </Th>
              <Th isNumeric>Reorder Point</Th>
              <Th isNumeric>Safety Stock</Th>
              <Th>Stock Level</Th>
              <Th
                isNumeric
                cursor="pointer"
                onClick={() => handleSort('quantityNeeded')}
                _hover={{ bg: tableHoverBg }}
              >
                Qty Needed{getSortIcon('quantityNeeded')}
              </Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredStockLevels.map((item) => (
              <Tr
                key={item.itemId}
                _hover={{ bg: tableHoverBg }}
              >
                <Td>
                  <Tooltip label={`${item.urgency.level.toUpperCase()} priority`}>
                    <Badge
                      colorScheme={item.urgency.color}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      w="fit-content"
                    >
                      {item.urgency.level === 'critical' && (
                        <Icon as={FaExclamationTriangle} />
                      )}
                      {item.urgency.level.toUpperCase()}
                    </Badge>
                  </Tooltip>
                </Td>
                <Td fontWeight="medium">{item.itemName}</Td>
                <Td isNumeric>
                  <Text
                    fontWeight="bold"
                    color={
                      item.urgency.level === 'critical'
                        ? 'red.500'
                        : item.urgency.level === 'high'
                        ? 'orange.500'
                        : 'yellow.500'
                    }
                  >
                    {item.currentStock}
                  </Text>
                </Td>
                <Td isNumeric>{item.reorderPoint}</Td>
                <Td isNumeric>
                  <Text color="blue.500" fontWeight="semibold">
                    {item.safetyStock}
                  </Text>
                </Td>
                <Td>
                  <VStack spacing={1} align="stretch">
                    <Progress
                      value={parseFloat(item.stockPercentage)}
                      size="sm"
                      colorScheme={
                        item.urgency.level === 'critical'
                          ? 'red'
                          : item.urgency.level === 'high'
                          ? 'orange'
                          : 'yellow'
                      }
                      borderRadius="full"
                      hasStripe
                      isAnimated
                    />
                    <Text fontSize="xs" color="gray.500">
                      {item.stockPercentage}%
                    </Text>
                  </VStack>
                </Td>
                <Td isNumeric>
                  <Badge colorScheme="purple" fontSize="sm">
                    {item.quantityNeeded}
                  </Badge>
                </Td>
                <Td>
                  <Tooltip label="Create purchase order">
                    <IconButton
                      icon={<Icon as={FaShoppingCart} />}
                      size="sm"
                      colorScheme="blue"
                      variant="ghost"
                      onClick={() => handleReorder(item)}
                      aria-label="Reorder"
                    />
                  </Tooltip>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Card p={5} h="100%" borderWidth="1px" borderColor={borderColor}>
      <VStack spacing={5} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center" flexWrap="wrap">
          <Box>
            <Heading size="md" color={textColor} mb={1}>
              Dynamic Reorder Alerts
            </Heading>
            {lastRefresh && (
              <Text fontSize="xs" color="gray.500">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </Text>
            )}
          </Box>
          <Button
            leftIcon={<Icon as={FaSync} />}
            size="sm"
            onClick={handleRefresh}
            isLoading={refreshing}
            variant="outline"
          >
            Refresh
          </Button>
        </Flex>

        {/* Summary Stats */}
        {stockLevels.length > 0 && (
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Stat
              bg={criticalStatBg}
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor="red.500"
              size="sm"
            >
              <StatLabel fontSize="xs">Critical Items</StatLabel>
              <StatNumber color="red.500">{stats.critical}</StatNumber>
              <StatHelpText fontSize="xs">
                <Icon as={FaExclamationTriangle} mr={1} />
                Immediate action required
              </StatHelpText>
            </Stat>

            <Stat
              bg={warningStatBg}
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor="orange.500"
              size="sm"
            >
              <StatLabel fontSize="xs">High Priority</StatLabel>
              <StatNumber color="orange.500">{stats.high}</StatNumber>
              <StatHelpText fontSize="xs">
                <Icon as={FaChartLine} mr={1} />
                Reorder soon
              </StatHelpText>
            </Stat>

            <Stat
              bg={cardBg}
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor={borderColor}
              size="sm"
            >
              <StatLabel fontSize="xs">Est. Reorder Cost</StatLabel>
              <StatNumber fontSize="lg">
                ${stats.totalValue.toLocaleString()}
              </StatNumber>
              <StatHelpText fontSize="xs">
                <Icon as={FaShoppingCart} mr={1} />
                Total needed
              </StatHelpText>
            </Stat>
          </SimpleGrid>
        )}

        {/* Description */}
        <Text fontSize="sm" color="gray.500">
          Items that have fallen below their dynamically calculated reorder point based on 
          consumption patterns and lead times.
        </Text>

        {/* Filters */}
        {stockLevels.length > 0 && (
          <HStack spacing={3}>
            <InputGroup size="sm" flex="1">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Select
              size="sm"
              w="180px"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              icon={<FaFilter />}
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical Only</option>
              <option value="high">High Only</option>
              <option value="medium">Medium Only</option>
              <option value="low">Low Only</option>
            </Select>
          </HStack>
        )}

        {/* Content */}
        {renderContent()}

        {/* Footer Note */}
        {filteredStockLevels.length > 0 && (
          <Text fontSize="xs" color="gray.500" textAlign="center">
            Reorder points are dynamically calculated based on demand patterns. 
            Click column headers to sort. Click cart icon to initiate reorder.
          </Text>
        )}
      </VStack>
    </Card>
  );
};

export default DynamicStockLevelsWidget;